import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../../components/CallexLoader';
import Pagination from '../../../../components/form/Pagination';
import AdminHeader from '../../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../../components/Modals/DeleteConfirmationModal';

import {
  productDelete,
  productListGet,
  useProductList
} from '../../../../store/slices/tenantSlices/productSlice';
import AddEditProductModal from './AddEditProductModal';
import ImportProductModal from './ImportProductModal';
import { checkModulePermission } from '../../../../utils/common';
import { useUserRolePermission } from '../../../../store/slices/authSlice';

const Product = () => {
  const dispatch = useDispatch();
  const productList = useProductList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchProductList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(productListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteProduct = async () => {
    try {
      const response = await dispatch(productDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (productList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchProductList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchProductList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Product"
        isExpandSearch
        isAdd
        onAddClick={() => setOpenAddModal(true)}
        isImport
        isImportDisabled={!checkModulePermission('is_import', 'Product', userRolePermission)}
        isAddDisabled={!checkModulePermission('is_add', 'Product', userRolePermission)}
        onImportClick={() => setOpenImportModal(true)}
        addBtnName="Add Product"
        searchPlaceholder="Search Product"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : productList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Product', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Product Type</th>
                  <th>UOM</th>
                  <th>Rate</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {productList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.name}</td>
                      <td>{val?.product_type}</td>
                      <td>{val?.uom}</td>
                      <td>{val?.rate}</td>
                      <td>{String(val?.status)}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Product', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', 'Product', userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Product', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Product', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={productList?.total}
              limit={productList?.limit}
              page={productList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchProductList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteProduct()}
      />
      <AddEditProductModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchProductList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <ImportProductModal
        isOpen={openImportModal}
        onCloseClick={(apiCall = false) => {
          setOpenImportModal(false);
          if (apiCall) {
            fetchProductList();
          }
        }}
      />
    </div>
  );
};

export default Product;
